// Staff-friendly wizard for assigning an already-created instructor to a class.
// Rendered in place of the generic workflow panel when the active workflow is
// `instructor_assignment`. The instructor identity is carried over from the
// originating workflow (e.g. instructor_onboarding) via workflow context, so
// the admin never has to re-identify the instructor.
//
// Four-step flow:
//   1) Confirm who's being assigned (auto-loaded from context)
//   2) Pick a class from the live list
//   3) Choose assignment type + effective date
//   4) Review + confirm
import { useEffect, useMemo, useState } from "react";
import { formatPeriodLabel } from "@/lib/registrationPeriodLabel";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Input } from "@/components/ui/input";
import { supabase } from "@/integrations/supabase/client";
import { classes as staticClasses } from "@/data/classes";
import { LIVE_ASSIGNMENT_STATUSES } from "@/lib/staffing";
import { toast } from "sonner";
import { friendlyError } from "@/lib/friendlyError";
import {
  ArrowRight,
  ArrowLeft,
  ChevronDown,
  ChevronUp,
  Loader2,
  Save,
  X,
  CheckCircle2,
  Search,
  UserCheck,
} from "lucide-react";
import type { WorkflowView } from "./CurrentWorkflowPanel";

interface Props {
  workflow: WorkflowView;
  functionName: string;
  onChange: (workflow: WorkflowView | null) => void;
  forceOpenNonce?: number;
}

type ClassRow = {
  id: string;
  slug: string | null;
  title: string;
  location: string | null;
  preferred_days: string | null;
  preferred_time: string | null;
  schedule: string | null;
  billing_unit: string | null;
  capacity: number;
  status: string;
  instructor_id: string | null;
};

type Step = 1 | 2 | 3 | 4;
const STEPS: { key: Step; label: string }[] = [
  { key: 1, label: "Instructor" },
  { key: 2, label: "Pick class" },
  { key: 3, label: "Semester & campus" },
  { key: 4, label: "Confirm" },
];

const KIND_OPTIONS = [
  { value: "regular", label: "Primary instructor" },
  { value: "co_teacher", label: "Co-instructor" },
  { value: "substitute", label: "Substitute" },
  { value: "temporary", label: "Temporary coverage" },
  { value: "guest", label: "Guest instructor" },
];

const staticClassRows: ClassRow[] = staticClasses
  .map((c) => ({

    id: c.id,
    slug: c.id,
    title: c.title,
    location: "Grace Community Church",
    preferred_days: c.schedule,
    preferred_time: null,
    schedule: c.schedule,
    billing_unit: c.sessions?.map((s) => s.label).join(", ") ?? c.startDate,
    capacity: 10,
    status: c.status,
    instructor_id: null,
  }));

export function InstructorAssignmentWizard({
  workflow,
  functionName,
  onChange,
  forceOpenNonce = 0,
}: Props) {
  const [open, setOpen] = useState(true);
  const [busy, setBusy] = useState<null | "confirm" | "save" | "cancel">(null);
  const [classes, setClasses] = useState<ClassRow[]>([]);
  const [classesLoading, setClassesLoading] = useState(false);
  const [classFilter, setClassFilter] = useState("");
  const [classStatusFilter, setClassStatusFilter] = useState<"all" | "open" | "coming_soon" | "unassigned">("all");
  const [manualStep, setManualStep] = useState<Step | null>(null);
  const [periods, setPeriods] = useState<Array<{ id: string; name: string; display_label: string | null; start_date: string | null; end_date: string | null; is_default: boolean; period_type: string | null; month_number: number | null; year: number | null; academic_year: string | null }>>([]);
  const [recentGlobal, setRecentGlobal] = useState<Array<{ class_slug: string; class_title: string | null; class_id: string | null }>>([]);
  const [recentByInstructor, setRecentByInstructor] = useState<Array<{ class_slug: string; class_title: string | null; class_id: string | null }>>([]);
  const [existingInPeriod, setExistingInPeriod] = useState<Array<{ id: string; instructor_id: string; assignment_kind: string; status: string; start_date: string | null; end_date: string | null }>>([]);

  useEffect(() => {
    if (forceOpenNonce > 0) setOpen(true);
  }, [forceOpenNonce]);

  const collected = workflow.collected ?? {};
  const ctx = workflow.context ?? {};
  const instructorId: string | undefined = ctx.instructor_id || collected.instructor_id;
  const instructorName: string =
    (ctx.instructor_name as string) ||
    (collected.instructor_name as string) ||
    (collected.instructor_query as string) ||
    (collected.full_name as string) ||
    "this instructor";

  const selectedClassSlug: string | undefined = collected.class_slug;
  const selectedClassTitle: string | undefined = collected.class_title;
  const assignmentKind: string = collected.assignment_kind || "regular";
  const effectiveDate: string | undefined = collected.effective_date;
  const registrationPeriodId: string | undefined = collected.registration_period_id;
  const registrationPeriodName: string | undefined = collected.registration_period_name;
  const campus: string | undefined = collected.campus;
  const room: string | undefined = collected.room;

  const hasConcreteInstructorName = instructorName !== "this instructor";
  const hasInstructor = Boolean(instructorId || hasConcreteInstructorName);
  const hasClass = Boolean(selectedClassSlug);
  const hasSemester = Boolean(registrationPeriodId || effectiveDate);
  const readyToConfirm = hasInstructor && hasClass && hasSemester && workflow.status !== "completed";
  const completed = workflow.status === "completed";

  const suggestedStep: Step = completed
    ? 4
    : !hasInstructor
    ? 1
    : !hasClass
    ? 2
    : !hasSemester
    ? 3
    : 4;
  const currentStep: Step = completed ? 4 : manualStep ?? suggestedStep;

  useEffect(() => {
    if (completed) return;
    if (!hasInstructor) setManualStep(1);
    else if (!hasClass) setManualStep(2);
  }, [completed, hasInstructor, hasClass, workflow.id]);

  // Load class list on mount (or when advancing to step 2).
  useEffect(() => {
    let cancelled = false;
    async function load() {
      setClassesLoading(true);
      const { data, error } = await (supabase as any)
        .from("classes")
        .select(
          "id, slug, title, location, preferred_days, preferred_time, schedule, billing_unit, capacity, status, instructor_id",
        )
        .eq("hidden", false)
        .order("title", { ascending: true });
      if (cancelled) return;
      if (error) {
        friendlyError({ what: "I couldn't load your class list.", nextStep: "Try again in a moment.", technical: error });
      } else {
        const rows = (data ?? []) as ClassRow[];
        setClasses(rows.length > 0 ? rows : staticClassRows);
      }
      setClassesLoading(false);
    }
    if (hasInstructor && !completed) load();
    return () => {
      cancelled = true;
    };
  }, [hasInstructor, completed]);

  // Load registration periods (semesters)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data } = await (supabase as any)
        .from("registration_periods")
        .select("id, name, display_label, start_date, end_date, is_default, sort_order, period_type, month_number, year, academic_year")
        .eq("is_active", true)
        .order("sort_order", { ascending: true });
      if (!cancelled && data) setPeriods(data);
    })();
    return () => { cancelled = true; };
  }, []);

  // Load recent assignments — globally + for this instructor — to power quick chips
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data: globalRows } = await (supabase as any)
        .from("instructor_class_assignments")
        .select("class_slug, class_title, class_id, created_at")
        .order("created_at", { ascending: false })
        .limit(50);
      if (!cancelled && globalRows) {
        const seen = new Set<string>();
        const uniq: Array<{ class_slug: string; class_title: string | null; class_id: string | null }> = [];
        for (const r of globalRows) {
          if (!r.class_slug || seen.has(r.class_slug)) continue;
          seen.add(r.class_slug);
          uniq.push({ class_slug: r.class_slug, class_title: r.class_title, class_id: r.class_id });
          if (uniq.length >= 5) break;
        }
        setRecentGlobal(uniq);
      }

      if (instructorId) {
        const { data: mineRows } = await (supabase as any)
          .from("instructor_class_assignments")
          .select("class_slug, class_title, class_id, created_at")
          .eq("instructor_id", instructorId)
          .order("created_at", { ascending: false })
          .limit(20);
        if (!cancelled && mineRows) {
          const seen = new Set<string>();
          const uniq: Array<{ class_slug: string; class_title: string | null; class_id: string | null }> = [];
          for (const r of mineRows) {
            if (!r.class_slug || seen.has(r.class_slug)) continue;
            seen.add(r.class_slug);
            uniq.push({ class_slug: r.class_slug, class_title: r.class_title, class_id: r.class_id });
            if (uniq.length >= 5) break;
          }
          setRecentByInstructor(uniq);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [instructorId]);

  // Duplicate detection: existing assignments for this class overlapping the chosen semester
  useEffect(() => {
    let cancelled = false;
    if (!selectedClassSlug || !registrationPeriodId) { setExistingInPeriod([]); return; }
    const period = periods.find((p) => p.id === registrationPeriodId);
    (async () => {
      let query = (supabase as any)
        .from("instructor_class_assignments")
        .select("id, instructor_id, assignment_kind, status, start_date, end_date")
        .eq("class_slug", selectedClassSlug)
        .in("status", [...LIVE_ASSIGNMENT_STATUSES]);
      if (period?.start_date) query = query.or(`end_date.is.null,end_date.gte.${period.start_date}`);
      if (period?.end_date) query = query.or(`start_date.is.null,start_date.lte.${period.end_date}`);
      const { data } = await query;
      if (!cancelled) setExistingInPeriod(data ?? []);
    })();
    return () => { cancelled = true; };
  }, [selectedClassSlug, registrationPeriodId, periods]);


  const filteredClasses = useMemo(() => {
    const q = classFilter.trim().toLowerCase();
    return classes.filter((c) => {
      if (classStatusFilter === "open" && !["published", "open"].includes(c.status)) return false;
      if (classStatusFilter === "coming_soon" && !["coming_soon", "coming-soon"].includes(c.status)) return false;
      if (classStatusFilter === "unassigned" && c.instructor_id) return false;
      if (!q) return true;
      return (
      [c.title, c.slug, c.location, c.preferred_days, c.billing_unit]
        .filter(Boolean)
        .some((f) => String(f).toLowerCase().includes(q))
      );
    });
  }, [classes, classFilter, classStatusFilter]);

  const selectedClass = classes.find((x) => (x.slug || x.id) === selectedClassSlug);
  const progressValue = completed
    ? 100
    : Math.max(25, Math.round(((currentStep - 1) / STEPS.length) * 100));

  const call = async (action: string, extra: Record<string, unknown> = {}) => {
    const { data, error } = await supabase.functions.invoke(functionName, {
      body: { action, draft_id: workflow.id, ...extra },
    });
    if (error) throw new Error(error.message);
    return data;
  };

  const patchCollected = async (patch: Record<string, any>) => {
    try {
      const r = await call("workflow_update_field", { collected_patch: patch });
      onChange(r?.workflow ?? null);
    } catch (e: unknown) {
      friendlyError({ what: "I couldn't save your last change.", technical: e });
    }
  };

  // Repair older assignment drafts that only carried `full_name` from the
  // onboarding workflow. The admin should still land on "Choose a Class" with
  // a live list instead of a dead "Still needed" state.
  useEffect(() => {
    if (!hasConcreteInstructorName || completed) return;
    const needsCollectedName = !collected.instructor_query || !collected.instructor_name;
    const needsContext = !ctx.instructor_id || !ctx.instructor_name;
    if (!needsCollectedName && !needsContext) return;

    let cancelled = false;
    async function hydrateInstructor() {
      const collectedPatch: Record<string, any> = {};
      const contextPatch: Record<string, any> = {};
      if (!collected.instructor_query) collectedPatch.instructor_query = instructorName;
      if (!collected.instructor_name) collectedPatch.instructor_name = instructorName;
      if (!ctx.instructor_name) contextPatch.instructor_name = instructorName;

      if (!ctx.instructor_id) {
        const { data } = await (supabase as any)
          .from("instructors")
          .select("id, full_name")
          .ilike("full_name", instructorName)
          .order("updated_at", { ascending: false })
          .limit(1)
          .maybeSingle();
        if (cancelled) return;
        if (data?.id) {
          contextPatch.instructor_id = data.id;
          contextPatch.instructor_name = data.full_name || instructorName;
          collectedPatch.instructor_name = data.full_name || instructorName;
          collectedPatch.instructor_query = data.full_name || instructorName;
        }
      }

      if (Object.keys(collectedPatch).length === 0 && Object.keys(contextPatch).length === 0) return;
      try {
        const r = await call("workflow_update_field", {
          collected_patch: collectedPatch,
          context_patch: contextPatch,
        });
        if (!cancelled) onChange(r?.workflow ?? null);
      } catch {
        // Non-blocking: the wizard can still proceed by instructor name.
      }
    }
    hydrateInstructor();
    return () => {
      cancelled = true;
    };
  }, [
    completed,
    hasConcreteInstructorName,
    instructorName,
    workflow.id,
    collected.instructor_query,
    collected.instructor_name,
    ctx.instructor_id,
    ctx.instructor_name,
  ]);

  const selectClass = (c: { id?: string; slug?: string | null; title: string; location?: string | null }) => {
    patchCollected({
      class_slug: c.slug || c.id,
      class_id: c.id ?? null,
      class_title: c.title,
      // Auto-fill campus from the class template unless already set
      campus: campus ?? c.location ?? null,
      instructor_query: instructorName,
      instructor_name: instructorName,
    });
  };

  const clearClass = () =>
    patchCollected({ class_slug: null, class_id: null, class_title: null });

  const setKind = (kind: string) => patchCollected({ assignment_kind: kind });
  const setEffective = (date: string) => patchCollected({ effective_date: date });
  const setPeriod = (id: string) => {
    const p = periods.find((x) => x.id === id);
    patchCollected({
      registration_period_id: id || null,
      registration_period_name: p ? formatPeriodLabel(p) : null,
      // Default effective_date to the period's start_date if not yet chosen
      ...(p?.start_date && !effectiveDate ? { effective_date: p.start_date } : {}),
    });
  };
  const setCampus = (v: string) => patchCollected({ campus: v || null });
  const setRoom = (v: string) => patchCollected({ room: v || null });

  const doConfirm = async () => {
    if (!readyToConfirm) return;
    setBusy("confirm");
    try {
      if (workflow.status === "draft") {
        const r = await call("workflow_prepare_review");
        onChange(r?.workflow ?? null);
      }
      const r = await call("workflow_execute");
      onChange(r?.view ?? null);
      const results = r?.results ?? [];
      const failed = results.filter((x: any) => !x.ok);
      if (failed.length === 0) {
        toast.success(
          `${instructorName} has been assigned to ${selectedClassTitle}.`,
        );
      } else {
        friendlyError({ what: `I couldn't finish assigning ${instructorName}.`, meaning: "Some steps didn't complete.", changed: "The completed steps have been saved.", nextStep: "Review the flagged step and try again.", technical: failed[0].error });
      }
    } catch (e: unknown) {
      friendlyError({ what: `I couldn't finish assigning ${instructorName}.`, technical: e });
    } finally {
      setBusy(null);
    }
  };

  const doSave = async () => {
    setBusy("save");
    try {
      const r = await call("workflow_save");
      onChange(r?.workflow ?? null);
      toast.success("Saved. Pick up this assignment any time.");
    } catch (e: unknown) {
      friendlyError({ what: "I couldn't save this assignment draft.", technical: e });
    } finally {
      setBusy(null);
    }
  };

  const doCancel = async () => {
    if (!confirm(`Cancel this assignment for ${instructorName}?`)) return;
    setBusy("cancel");
    try {
      await call("workflow_cancel");
      onChange(null);
    } catch (e: unknown) {
      friendlyError({ what: "I couldn't cancel this assignment.", technical: e });
    } finally {
      setBusy(null);
    }
  };

  const kindLabel =
    KIND_OPTIONS.find((k) => k.value === assignmentKind)?.label || assignmentKind;

  return (
    <Card className="border-primary/30 bg-primary/5">
      {/* Header — always names the instructor */}
      <div className="flex items-center gap-2 px-3 py-2 border-b border-primary/20">
        <Badge variant="outline" className="text-[10px] uppercase">
          Teaching a class
        </Badge>
        <div className="font-medium text-sm truncate flex-1">
          {completed
            ? `${instructorName} → ${selectedClassTitle ?? "class"}`
            : `What class is ${instructorName} teaching?`}
        </div>
        {!completed && (
          <span className="text-xs text-muted-foreground tabular-nums">{progressValue}%</span>
        )}
        <Button
          variant="ghost"
          size="icon"
          className="h-6 w-6"
          onClick={() => setOpen(!open)}
          aria-label={open ? "Collapse" : "Expand"}
        >
          {open ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
        </Button>
      </div>

      {/* Step tracker */}
      <div className="px-3 pt-3">
        <ol className="flex items-center gap-1 text-[11px]" aria-label="Assignment steps">
          {STEPS.map((s, i) => {
            const state = s.key < currentStep ? "done" : s.key === currentStep ? "active" : "todo";
            return (
              <li key={s.key} className="flex items-center gap-1 min-w-0">
                <span
                  className={`inline-flex items-center justify-center h-5 w-5 rounded-full text-[10px] font-semibold shrink-0 ${
                    state === "done"
                      ? "bg-primary text-primary-foreground"
                      : state === "active"
                      ? "bg-primary/20 text-primary border border-primary"
                      : "bg-muted text-muted-foreground"
                  }`}
                >
                  {state === "done" ? "✓" : s.key}
                </span>
                <span
                  className={`truncate ${
                    state === "active" ? "font-medium text-foreground" : "text-muted-foreground"
                  }`}
                >
                  {s.label}
                </span>
                {i < STEPS.length - 1 && (
                  <ArrowRight className="h-3 w-3 text-muted-foreground/60 shrink-0" />
                )}
              </li>
            );
          })}
        </ol>
        <Progress value={progressValue} className="h-1.5 mt-2" />
      </div>

      {open && (
        <div className="px-3 py-3 space-y-3 text-sm">
          {/* Step 1 — Instructor confirmation */}
          <div className="rounded border bg-background p-3 flex items-center gap-2">
            <UserCheck className="h-4 w-4 text-primary shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="text-xs text-muted-foreground">Assigning</div>
              <div className="font-medium truncate">{instructorName}</div>
            </div>
            {hasInstructor ? (
              <Badge variant="secondary" className="text-[10px]">Ready</Badge>
            ) : (
              <Badge variant="destructive" className="text-[10px]">Missing instructor</Badge>
            )}
          </div>

          {!hasInstructor && (
            <p className="text-xs text-amber-700 dark:text-amber-400">
              The instructor identity was not carried over. Please close this and restart from the
              instructor's profile so we can carry the correct person into this assignment.
            </p>
          )}

          {/* Step 2 — Pick a class (existing first, new template as exception) */}
          {hasInstructor && !completed && currentStep === 2 && (
            <div className="space-y-3">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <div className="text-sm font-medium">
                    {hasClass ? "Selected class" : `What class is ${instructorName} teaching?`}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    Choose an existing class or create a new one.
                  </div>
                </div>
                {hasClass && (
                  <Button size="sm" variant="ghost" className="h-6 text-xs" onClick={clearClass}>
                    Change
                  </Button>
                )}
              </div>

              {hasClass && selectedClass && (
                <div className="rounded border border-primary/30 bg-primary/5 p-3">
                  <div className="text-xs text-muted-foreground">Selected</div>
                  <div className="font-medium">{selectedClass.title}</div>
                  <div className="text-xs text-muted-foreground mt-0.5 space-y-0.5">
                    {selectedClass.location && <div>Campus: {selectedClass.location}</div>}
                    {selectedClass.preferred_days && <div>Days: {selectedClass.preferred_days}</div>}
                    {selectedClass.preferred_time && <div>Time: {selectedClass.preferred_time}</div>}
                    {selectedClass.billing_unit && <div>Sessions: {selectedClass.billing_unit}</div>}
                    <div>Capacity: {selectedClass.capacity}</div>
                  </div>
                  <div className="pt-2">
                    <Button size="sm" onClick={() => setManualStep(3)}>
                      Continue <ArrowRight className="h-3.5 w-3.5 ml-1" />
                    </Button>
                  </div>
                </div>
              )}

              {!hasClass && (
                <>
                  {/* Frequently teaches — quick picks for this instructor */}
                  {recentByInstructor.length > 0 && (
                    <div>
                      <div className="text-[11px] font-semibold text-muted-foreground uppercase mb-1">
                        Frequently teaches
                      </div>
                      <div className="flex flex-wrap gap-1">
                        {recentByInstructor.map((r) => {
                          const match = classes.find((c) => (c.slug || c.id) === r.class_slug);
                          const title = match?.title || r.class_title || r.class_slug;
                          return (
                            <Button
                              key={`mine-${r.class_slug}`}
                              size="sm"
                              variant="outline"
                              className="h-7 text-xs"
                              onClick={() =>
                                selectClass(
                                  match ?? {
                                    id: r.class_id ?? undefined,
                                    slug: r.class_slug,
                                    title,
                                    location: null,
                                  },
                                )
                              }
                            >
                              ✓ {title}
                            </Button>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Recently assigned across the Hub */}
                  {recentGlobal.length > 0 && (
                    <div>
                      <div className="text-[11px] font-semibold text-muted-foreground uppercase mb-1">
                        Recently assigned
                      </div>
                      <div className="flex flex-wrap gap-1">
                        {recentGlobal.map((r) => {
                          const match = classes.find((c) => (c.slug || c.id) === r.class_slug);
                          const title = match?.title || r.class_title || r.class_slug;
                          return (
                            <Button
                              key={`recent-${r.class_slug}`}
                              size="sm"
                              variant="outline"
                              className="h-7 text-xs"
                              onClick={() =>
                                selectClass(
                                  match ?? {
                                    id: r.class_id ?? undefined,
                                    slug: r.class_slug,
                                    title,
                                    location: null,
                                  },
                                )
                              }
                            >
                              ✓ {title}
                            </Button>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  <div className="relative">
                    <Search className="h-3.5 w-3.5 absolute left-2 top-2.5 text-muted-foreground" />
                    <Input
                      value={classFilter}
                      onChange={(e) => setClassFilter(e.target.value)}
                      placeholder="Search classes…"
                      className="pl-7 h-8 text-xs"
                      autoFocus
                    />
                  </div>

                  <div className="flex flex-wrap gap-1" aria-label="Class filters">
                    {[
                      ["all", "All"],
                      ["open", "Open"],
                      ["coming_soon", "Coming soon"],
                      ["unassigned", "Unassigned"],
                    ].map(([value, label]) => (
                      <Button
                        key={value}
                        type="button"
                        size="sm"
                        variant={classStatusFilter === value ? "default" : "outline"}
                        className="h-7 text-xs"
                        onClick={() => setClassStatusFilter(value as typeof classStatusFilter)}
                      >
                        {label}
                      </Button>
                    ))}
                  </div>

                  <div className="max-h-56 overflow-y-auto rounded border bg-background divide-y">
                    {classesLoading ? (
                      <div className="p-3 text-xs text-muted-foreground flex items-center gap-2">
                        <Loader2 className="h-3 w-3 animate-spin" /> Loading classes…
                      </div>
                    ) : filteredClasses.length === 0 ? (
                      <div className="p-3 text-xs text-muted-foreground">
                        No matching classes. Create a new class template below.
                      </div>
                    ) : (
                      filteredClasses.map((c) => (
                        <button
                          key={c.id}
                          onClick={() => selectClass(c)}
                          className="w-full text-left px-3 py-2 hover:bg-primary/5 transition-colors"
                        >
                          <div className="font-medium text-xs">{c.title}</div>
                          <div className="text-[11px] text-muted-foreground truncate">
                            {[c.location, c.preferred_days, c.preferred_time, c.billing_unit]
                              .filter(Boolean)
                              .join(" · ") || "No campus set yet"}
                          </div>
                        </button>
                      ))
                    )}
                  </div>

                  <div className="rounded border border-dashed p-3 flex items-center justify-between gap-2 bg-muted/30">
                    <div className="text-xs text-muted-foreground">
                      Teaching something brand new?
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        window.open("/admin/classes#new", "_blank", "noopener");
                        toast.message(
                          "Create the new class template, then come back — this assignment is saved as a draft.",
                        );
                      }}
                    >
                      + Create new class template
                    </Button>
                  </div>

                  <div className="flex flex-wrap gap-2 pt-1">
                    <Button size="sm" variant="outline" onClick={doSave} disabled={!!busy}>
                      {busy === "save" ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Save className="h-3.5 w-3.5 mr-1" />}
                      Finish later
                    </Button>
                  </div>
                </>
              )}
            </div>
          )}

          {/* Step 3 — Semester, campus, room */}
          {hasClass && !completed && currentStep === 3 && (
            <div className="space-y-3">
              <div>
                <div className="text-sm font-medium">Which semester and campus?</div>
                <div className="text-xs text-muted-foreground">
                  Everything else is inherited from the class template.
                </div>
              </div>

              <div className="grid gap-2 sm:grid-cols-2">
                <div>
                  <label className="text-xs text-muted-foreground">Semester / registration period</label>
                  <select
                    value={registrationPeriodId ?? ""}
                    onChange={(e) => setPeriod(e.target.value)}
                    className="w-full h-8 rounded border bg-background px-2 text-xs"
                  >
                    <option value="">Choose a semester…</option>
                    {periods.map((p) => (
                      <option key={p.id} value={p.id}>
                        {formatPeriodLabel(p) + (p.is_default ? " (current)" : "")}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-xs text-muted-foreground">Campus</label>
                  <Input
                    value={campus ?? ""}
                    onChange={(e) => setCampus(e.target.value)}
                    placeholder={selectedClass?.location ?? "Grace Community Church"}
                    className="h-8 text-xs"
                  />
                </div>
                <div>
                  <label className="text-xs text-muted-foreground">Room (optional override)</label>
                  <Input
                    value={room ?? ""}
                    onChange={(e) => setRoom(e.target.value)}
                    placeholder="Same as template"
                    className="h-8 text-xs"
                  />
                </div>
                <div>
                  <label className="text-xs text-muted-foreground">First day</label>
                  <Input
                    type="date"
                    value={effectiveDate ?? ""}
                    onChange={(e) => setEffective(e.target.value)}
                    className="h-8 text-xs"
                  />
                </div>
                <div className="sm:col-span-2">
                  <label className="text-xs text-muted-foreground">Assignment type</label>
                  <select
                    value={assignmentKind}
                    onChange={(e) => setKind(e.target.value)}
                    className="w-full h-8 rounded border bg-background px-2 text-xs"
                  >
                    {KIND_OPTIONS.map((k) => (
                      <option key={k.value} value={k.value}>{k.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Duplicate detection */}
              {existingInPeriod.length > 0 && (
                <div className="rounded border border-amber-300 bg-amber-50 dark:bg-amber-950/30 dark:border-amber-800 p-3 space-y-2 text-xs">
                  <div className="font-medium text-amber-900 dark:text-amber-200">
                    This class already has an assignment for {registrationPeriodName ?? "this semester"}.
                  </div>
                  <div className="text-amber-800 dark:text-amber-300">
                    You can add a second section (co-instructor), replace the current instructor, or open the existing assignment instead of creating a duplicate.
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-7 text-xs"
                      onClick={() => setKind("co_teacher")}
                    >
                      Create an additional section
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-7 text-xs"
                      onClick={() => setKind("substitute")}
                    >
                      Replace instructor
                    </Button>
                  </div>
                </div>
              )}

              <div className="flex flex-wrap gap-2 pt-1">
                <Button size="sm" variant="outline" onClick={() => setManualStep(2)}>
                  <ArrowLeft className="h-3.5 w-3.5 mr-1" />
                  Back
                </Button>
                <Button size="sm" onClick={() => setManualStep(4)} disabled={!hasSemester}>
                  Continue
                  <ArrowRight className="h-3.5 w-3.5 ml-1" />
                </Button>
                <Button size="sm" variant="outline" onClick={doSave} disabled={!!busy}>
                  {busy === "save" ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Save className="h-3.5 w-3.5 mr-1" />}
                  Finish later
                </Button>
              </div>
            </div>
          )}

          {/* Step 4 — Confirm */}
          {hasClass && !completed && currentStep === 4 && (
            <div className="rounded border border-primary/20 bg-background p-3 space-y-1">
              <div className="text-xs font-semibold text-muted-foreground uppercase mb-1">
                Confirm
              </div>
              <ReviewRow label="Instructor" value={instructorName} />
              <ReviewRow label="Class" value={selectedClassTitle ?? "—"} />
              <ReviewRow label="Semester" value={registrationPeriodName ?? "—"} />
              <ReviewRow label="Campus" value={campus ?? selectedClass?.location ?? "—"} />
              {room && <ReviewRow label="Room" value={room} />}
              <ReviewRow label="Type" value={kindLabel} />
              <ReviewRow
                label="Starts"
                value={effectiveDate ? formatDate(effectiveDate) : "Pick a date"}
              />
            </div>
          )}

          {completed && (
            <div className="rounded border border-primary/20 bg-background p-3 flex items-start gap-2">
              <CheckCircle2 className="h-4 w-4 text-primary mt-0.5 shrink-0" />
              <div className="text-sm">
                <div className="font-medium">
                  {instructorName} is now assigned to {selectedClassTitle}.
                </div>
                <div className="text-xs text-muted-foreground mt-1">
                  You can view or adjust this from the instructor's profile.
                </div>
              </div>
            </div>
          )}

          {/* Actions */}
          {!completed && currentStep === 4 && (
            <div className="flex flex-wrap gap-2 pt-1">
              <Button size="sm" variant="outline" onClick={() => setManualStep(3)} disabled={!!busy}>
                <ArrowLeft className="h-3.5 w-3.5 mr-1" />
                Back
              </Button>
              <Button
                size="sm"
                onClick={doConfirm}
                disabled={!readyToConfirm || !!busy}
                title={
                  !hasInstructor
                    ? "Missing instructor"
                    : !hasClass
                    ? "Pick a class to continue."
                    : !hasSemester
                    ? "Choose a semester to continue."
                    : undefined
                }
              >
                {busy === "confirm" ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" />
                ) : (
                  <CheckCircle2 className="h-3.5 w-3.5 mr-1" />
                )}
                Confirm Assignment
              </Button>
              <Button size="sm" variant="outline" onClick={doSave} disabled={!!busy}>
                {busy === "save" ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" />
                ) : (
                  <Save className="h-3.5 w-3.5 mr-1" />
                )}
                Finish later
              </Button>
              <Button size="sm" variant="ghost" onClick={doCancel} disabled={!!busy}>
                <X className="h-3.5 w-3.5 mr-1" /> Cancel
              </Button>
              {!readyToConfirm && (
                <span className="text-xs text-muted-foreground self-center">
                  {!hasClass ? "Pick a class to continue." : !hasSemester ? "Choose a semester to continue." : "Missing required info."}
                </span>
              )}
            </div>
          )}
        </div>
      )}
    </Card>
  );
}

function ReviewRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="text-xs flex gap-2">
      <span className="text-muted-foreground min-w-[80px]">{label}:</span>
      <span className="font-medium">{value}</span>
    </div>
  );
}

function formatDate(iso: string) {
  try {
    const d = new Date(`${iso}T00:00:00`);
    return d.toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" });
  } catch {
    return iso;
  }
}
